import React, { useContext } from "react";
import { Rnd } from "react-rnd";
import parse from "html-react-parser";
import FroalaEditor from "../../Uitls/CommonFroalaEditor";
import {
  getSelectionOutlineOffset,
  toNumericStyleValue,
} from "../../Uitls/Util";
import { useEditorMainPageContext } from "../contexts/EditorMainPageContext";
import ValueContext from "../../ContextAPi/appContext";
import { getRhsFontOptionCssStack } from "../../Uitls/CommonFroalaConfig";

export const EditorCanvasButtonItem = ({ items, index }) => {
  const {
    playPauseRef,
    isEditorOn: currentEditorState,
    canvasDimension,
    isAspectRatioLocked,
    handleComponentsDrag,
    handleComponentsDragStop,
    callOnResizeStop,
    updateFontOnResize,
    multiSelectedComps,
    selectedComponent,
    canvasStyleDraft,
    setDisplayColorPicker,
    isDragged,
    layerIndex,
    froalaEditorHandleOnclciksAndDrag,
    setFroalaIndex,
    setContainerID,
    sendSelectedComponent,
    setCurrentIdZindex,
    setVisibility,
    handleCtrlClick,
    setEditComponent,
    editComponentRef,
    handleDeleteKey,
    playPause,
    createFroalaInstance,
    handleTextChangeInLayerComp,
  } = useEditorMainPageContext();
  const savedCustomFontList =
    useContext(ValueContext)?.savedCustomFontList ?? [];
  let isEditorOn = currentEditorState;

  if (items?.componentType !== "Button") return null;

  // While selected, prefer RHS/selectedComponent styles so palette changes
  // paint live on canvas even if layerCompData commit lags. canvasStyleDraft
  // wins for fill/border so picker feedback loops cannot snap back mid-drag.
  const isSelected = String(selectedComponent?.id) === String(items.id);
  const draftForItem =
    canvasStyleDraft && String(canvasStyleDraft.id) === String(items.id)
      ? canvasStyleDraft
      : null;
  const liveStyles = (() => {
    if (!isSelected && !draftForItem) return items.componentStyles;
    const merged = {
      ...items.componentStyles,
      ...(isSelected && selectedComponent?.componentStyles
        ? selectedComponent.componentStyles
        : null),
    };
    if (draftForItem?.backgroundColor != null) {
      merged.backgroundColor = draftForItem.backgroundColor;
    }
    if (draftForItem?.border != null) {
      merged.border = draftForItem.border;
    }
    return merged;
  })();

  const buttonTextJustifyContent =
    liveStyles.textAlign === "left"
      ? "start"
      : liveStyles.textAlign === "right"
        ? "end"
        : "center";

  return (
    <Rnd
      resizeHandleClasses={{
        topRight: "topAnchorRight",
        topLeft: "topAnchorLeft",
        bottomLeft: "bottomAnchorLeft",
        bottomRight: "bottomAnchorRight",
        top: "horizontalAnchorCenter",
        bottom: "horizontalAnchorCenter",
        left: "verticallAnchorCenter",
        right: "verticallAnchorCenter",
      }}
      disableDragging={(() => {
        return (
          liveStyles?.hideBoolean ||
          liveStyles?.lockBoolean ||
          !playPauseRef.current.animationCompleted ||
          isEditorOn === "true" ||
          isEditorOn === "dragged"
        );
      })()}
      enableResizing={
        !liveStyles?.hideBoolean &&
        !liveStyles?.lockBoolean &&
        playPauseRef.current.animationCompleted &&
        isEditorOn !== "true" &&
        isEditorOn !== "dragged"
      }
      size={{
        width: liveStyles.width,
        height: liveStyles.height,
      }}
      scale={canvasDimension.scale}
      position={{
        x:
          toNumericStyleValue(liveStyles.left) +
          (parseInt(liveStyles.translateX) || 0),
        y:
          toNumericStyleValue(liveStyles.top) +
          (parseInt(liveStyles.translateY) || 0),
      }}
      enableUserSelectHack={false}
      lockAspectRatio={isAspectRatioLocked}
      onDrag={(e, d) => {
        handleComponentsDrag(d, items);
      }}
      onDragStop={(e, d) => {
        handleComponentsDragStop(e, d, items, index);
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        callOnResizeStop({
          id: items.id,
          width: ref.offsetWidth,
          height: ref.offsetHeight,
          x: position.x - (parseInt(liveStyles.translateX) || 0),
          y: position.y - (parseInt(liveStyles.translateY) || 0),
        });
      }}
      onResize={(e, direction, ref, delta, position) => {
        updateFontOnResize({
          id: items.id,
          width: ref.offsetWidth,
          height: ref.offsetHeight,
          x: position.x - (parseInt(liveStyles.translateX) || 0),
          y: position.y - (parseInt(liveStyles.translateY) || 0),
        });
      }}
      key={items.id || index}
      id={items.id}
      className={
        `${
          multiSelectedComps.length < 2 && isSelected
            ? "selectedComp div-dragresize-parent cs-container button-container buttonResize"
            : "div-dragresize-parent cs-container buttonResize"
        }` +
        " " +
        `${
          multiSelectedComps.length > 1 && items.multiSelected === true
            ? "multipleSelection"
            : ""
        }`
      }
      onClick={(e) => {
        e.preventDefault();
        setDisplayColorPicker(false);

        if (e.detail > 0) {
          if (e.detail === 3) return;
          if (isDragged.mouseMove && e.detail === 1) {
            isEditorOn = "dragged";
          } else if (isEditorOn === "dragged") {
            isEditorOn = "true";
          }
          if (index !== layerIndex) isEditorOn = "notClickedYet";
          froalaEditorHandleOnclciksAndDrag(e, index);
        }

        setFroalaIndex(index);
        setContainerID(items.id);
        sendSelectedComponent(e, index);
        setCurrentIdZindex(e.currentTarget.id);
        setDisplayColorPicker(false);
        setVisibility(false);
        handleCtrlClick(e, items);
        setEditComponent(undefined);
        editComponentRef.current = undefined;
      }}
      tabIndex="0"
      onKeyDown={(e) => handleDeleteKey(e)}
      style={{
        pointerEvents:
          (!playPause.animationCompleted || liveStyles.hideBoolean) && "none",
        fontFamily: getRhsFontOptionCssStack(
          liveStyles.fontFamily,
          savedCustomFontList,
        ),
        fontSize: liveStyles.fontSize + "px",
        fontWeight: liveStyles.boldClick
          ? liveStyles?.fontWeightBold
          : liveStyles?.fontWeight,
        textAlign: liveStyles.textAlign,
        color: liveStyles.color,
        zIndex: liveStyles.zIndex,
        letterSpacing: liveStyles.letterSpacing,
        lineHeight: liveStyles.lineHeight,
        targetLink: liveStyles.targetLink,
        transform: `rotate(${liveStyles.rotation}) rotateX(${liveStyles.rotationX}) rotateY(${liveStyles.rotationY}) scale(${liveStyles.scale})`,
        ease: liveStyles.ease,
        opacity: liveStyles.opacity,
        fontStyle: liveStyles.fontStyle,
        textTransform: liveStyles?.textTransform,
        outlineOffset: getSelectionOutlineOffset(liveStyles),
        webkitTransformOrigin: `${liveStyles.transformOriginX}% ${liveStyles.transformOriginY}% ${liveStyles.transformOriginZ}px`,
        textDecoration: liveStyles?.underlineClick
          ? liveStyles?.strikeThroughClicked
            ? `${liveStyles?.textDecUnderline} ${liveStyles?.textDecoration} `
            : liveStyles?.textDecUnderline
          : liveStyles?.textDecoration,
      }}
    >
      <div
        style={{
          overflow: "hidden",
          width: "100%",
          height: "100%",
        }}
      >
        <div
          data-button-fill="true"
          style={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            border: liveStyles.border,
            backgroundClip: "padding-box",
            borderTopLeftRadius: liveStyles.borderTopLeftRadius,
            borderTopRightRadius: liveStyles.borderTopRightRadius,
            borderBottomLeftRadius: liveStyles.borderBottomLeftRadius,
            borderBottomRightRadius: liveStyles.borderBottomRightRadius,
            paddingTop: liveStyles.paddingTop,
            paddingRight: liveStyles.paddingRight,
            paddingBottom: liveStyles.paddingBottom,
            paddingLeft: liveStyles.paddingLeft,
            backgroundColor: liveStyles.backgroundColor,
            display: "inline-grid",
            justifyContent: buttonTextJustifyContent,
            alignItems: "center",
            textDecoration: liveStyles?.underlineClick
              ? liveStyles?.strikeThroughClicked
                ? `${liveStyles?.textDecUnderline} ${liveStyles?.textDecoration} `
                : liveStyles?.textDecUnderline
              : liveStyles?.textDecoration,
          }}
        >
          {liveStyles.lockBoolean !== true ? (
            <FroalaEditor
              forwardedRef={(e) => (createFroalaInstance.current[index] = e)}
              handleModelChange={(e) => {
                handleTextChangeInLayerComp(e, index);
              }}
              className="button-froala-editor"
              defaultText={items.froalaText}
              tag="textarea"
              showToolBar={false}
            ></FroalaEditor>
          ) : (
            <div>{parse(items.froalaText || "")}</div>
          )}
        </div>
      </div>
    </Rnd>
  );
};
